/**
 * Dynamic Bundle Logic for Mix & Match Selector
 * Fully stabilized: Fixed mid-refresh block duplication and variant form matching.
 */

// Global state memory tracking map framework
let selectedBundleItems = new Map();

// --- GLOBAL ELEMENT REFERENCE HOOKS ---
let summaryCount = document.getElementById('bundle-count');
let summaryDiscount = document.getElementById('bundle-discount');
let summaryTotal = document.getElementById('bundle-total');
let bundleWrapper = document.querySelector('.bundle-selector-wrapper');

// Master form tracker node engine
function getActiveShopifyForm() {
    const elementsWithTemplateId = document.querySelectorAll('[id^="product-form-template--"]');
    let foundForm = null;

    for (const el of elementsWithTemplateId) {
        const id = el.getAttribute('id') || '';
        if (id.endsWith('__main') && (el.offsetWidth > 0 || el.offsetHeight > 0)) {
            foundForm = el.tagName === 'FORM' ? el : el.closest('form');
            if (foundForm) break; 
        }
    }

    if (foundForm) return foundForm;

    const genericForm = document.querySelector('form[data-type="add-to-cart-form"], [data-js-product-form], form[action^="/cart/add"]');
    if (genericForm) return genericForm;

    const childSelect = document.querySelector('select[name="id"]');
    if (childSelect) return childSelect.closest('form');

    return null;
}

// --- INITIALIZATION ---
document.addEventListener('DOMContentLoaded', initializeBundleListeners);
document.addEventListener('shopify:section:load', initializeBundleListeners); 

// Standalone Named Listeners to enforce single-binding rules on the browser engine
function handleFormVariantChange(event) {
    if (event.target.name === 'id' || event.target.name === 'quantity') {
        setTimeout(updateBundleSummary, 100); 
    }
}

function handleWrapperChangeSync(event) {
    if (event.target.classList.contains('bundle-selection-input')) {
        handleSelection(event.target);
    }
    if (event.target.classList.contains('bundle-variant-selector')) { 
        updateVariantSelection(event.target);
    }
}

function initializeBundleListeners() {
    // 🌟 THE DUPLICATE DOM ELEMENT DESTROYER 🌟
    // Locates all instances of your bundle layout running simultaneously on the page
    const visualBlocks = document.querySelectorAll('.product-page__block.bundle-builder-block');
    
    if (visualBlocks.length > 1) {
        console.warn(`DOM Clean: Identified ${visualBlocks.length} bundle layouts. Destroying duplicate blocks now...`);
        
        // Loop backwards through any copies and completely remove them from your browser window layout
        for (let i = visualBlocks.length - 1; i > 0; i--) {
            visualBlocks[i].remove(); 
        }
    }

    // Now re-assign your global helper anchors to point securely to the single surviving layout block
    summaryCount = document.getElementById('bundle-count');
    summaryDiscount = document.getElementById('bundle-discount');
    summaryTotal = document.getElementById('bundle-total');
    
    const freshWrapper = document.querySelector('.bundle-selector-wrapper');

    const mainProductForm = getActiveShopifyForm();
    if (!mainProductForm) {
        console.warn("Bundle Sync: Active form container not ready yet.");
        return;
    }

    // Scrub tracking attributes from old structural variant cycles
    if (mainProductForm.hasAttribute('data-bundle-listeners-attached')) {
        mainProductForm.removeAttribute('data-bundle-listeners-attached');
    }

    mainProductForm.removeEventListener('change', handleFormVariantChange);
    mainProductForm.addEventListener('change', handleFormVariantChange);

    mainProductForm.removeEventListener('click', handleUnifiedCartSubmission, true);
    mainProductForm.addEventListener('click', handleUnifiedCartSubmission, true);
    
    mainProductForm.removeEventListener('submit', handleUnifiedCartSubmission, true);
    mainProductForm.addEventListener('submit', handleUnifiedCartSubmission, true);

    mainProductForm.setAttribute('data-bundle-listeners-attached', 'true');

    initializeDefaultSelections();
    
    if (freshWrapper) {
        freshWrapper.removeEventListener('change', handleWrapperChangeSync);
        freshWrapper.addEventListener('change', handleWrapperChangeSync);
    }
   
    updateBundleSummary();
    attachCardListeners();
    saveBundleToLocalStorage();

    const firstPackageBtn = document.querySelector('.package-select-btn');
    if (firstPackageBtn && !firstPackageBtn.classList.contains('selected_item')) {
        console.log("System Sync: Automatically selecting first package block on load.");
        
        // Safely trigger a synthetic click event context to pass selections through your global handlers
        setTimeout(() => {
            firstPackageBtn.click();
        }, 200); // Small delay to let custom layout trees assemble completely clean
    }
}




function attachCardListeners() {
    // Re-verify targets matching the single remaining layout tree block
    const allCards = document.querySelectorAll('.bundle-item-card');
    
    allCards.forEach(card => {
        card.removeAttribute('data-card-listener-attached');
        
        // Skip assignment checks if the matching card element is sitting inside an orphaned shadow tree layout
        if (card.offsetWidth === 0 && card.offsetHeight === 0) {
            return;
        }
        
        // Guard against binding duplicate interactions to an active element block
        if (card.hasAttribute('data-card-listener-attached')) return;
        
        card.addEventListener('click', handleCardClick);
        card.setAttribute('data-card-listener-attached', 'true');
        
        const variantSelector = card.querySelector('.bundle-variant-selector');
        if (variantSelector) {
            variantSelector.addEventListener('click', (event) => {
                event.stopPropagation(); 
            });
        }
    });

    attachQuantityButtonListeners();
}
function handleQuantityActionToggle(event) {
    const button = event.target.closest('.quantity-btn');
    if (!button) return;

    event.stopPropagation();
    const quantityWrapper = button.closest('.quantity-field');
    if (!quantityWrapper) return;

    const displayInput = quantityWrapper.querySelector('.bundle-item-quantity-display');
    const productId = quantityWrapper.dataset.productId;

    let currentQty = parseInt(displayInput.value, 10) || 1;
    const minQty = parseInt(displayInput.min, 10) || 1;
    const maxQty = parseInt(displayInput.max, 10) || 99;

    let newQty = currentQty;
    if (button.classList.contains('quantity-btn--plus')) {
        newQty = Math.min(currentQty + 1, maxQty);
    } else if (button.classList.contains('quantity-btn--minus')) {
        newQty = Math.max(currentQty - 1, minQty);
    }

    if (newQty !== currentQty) {
        displayInput.value = newQty;
        if (selectedBundleItems.has(productId)) {
            const itemData = selectedBundleItems.get(productId);
            itemData.quantity = newQty;
            selectedBundleItems.set(productId, itemData);
        }
        updateBundleSummary(); 
    }
}

function attachQuantityButtonListeners() {
    const activeWrapper = document.querySelector('.bundle-selector-wrapper');
    if (activeWrapper) {
        activeWrapper.removeEventListener('click', handleQuantityActionToggle);
        activeWrapper.addEventListener('click', handleQuantityActionToggle);
    }
}

function handleCardClick(event) {
    if (event.target.closest('.quantity-btn') || 
        event.target.closest('.bundle-item-quantity-display') ||
        event.target.closest('.bundle-variant-selector')) {
        return; 
    }
    
    const card = event.currentTarget; 
    const input = card.querySelector('.bundle-selection-input');

    if (input) {
        if (input.type === 'checkbox') {
            input.checked = !input.checked; 
        } else if (input.type === 'radio') {
            input.checked = true;
        }
        handleSelection(input);
    }
}

function getDiscountPrice(optionElement) {
    const priceInCents = parseInt(optionElement.dataset.price || optionElement.dataset.defaultPrice, 10);
    return !isNaN(priceInCents) ? priceInCents : 0; 
}

function getItemPrice(optionElement) {
    const priceInCents = parseInt(optionElement.dataset.price || optionElement.dataset.orginalPrice || optionElement.dataset.originalPrice, 10);
    return !isNaN(priceInCents) ? priceInCents : 0; 
}

function updateVariantSelection(selector) {
  const card = selector.closest('.bundle-item-card');
  const productId = card.dataset.productId;
  const selectedOption = selector.options[selector.selectedIndex];
  const newVariantId = parseInt(selectedOption.value, 10);
  const selectedProductTitle = selectedOption.text.split(' - ')[0].trim();
  const newPrice = getDiscountPrice(selectedOption);
  const originalPrice = getItemPrice(selectedOption);

  const inputElement = card.querySelector('.bundle-selector-input-wrapper > .bundle-selection-input');
  const discountAmount = inputElement ? parseFloat(inputElement.dataset.discount || 0) : 0; 
  const newSku = selectedOption.dataset.sku || "";

  if (inputElement && inputElement.checked) {
    if (inputElement.type === 'radio' && !inputElement.checked) return;

    selectedBundleItems.set(productId, { 
      variantId: newVariantId, 
      price: newPrice,
      originalPrice: originalPrice,
      title: selectedProductTitle,
      discount: discountAmount,
      sku: newSku
    });
    updateBundleSummary();
  }
}

function initializeDefaultSelections() {
    const defaultRadioInputs = document.querySelectorAll('.bundle-selection-input[type="radio"]:checked');

    defaultRadioInputs.forEach(input => {
        const card = input.closest('.bundle-item-card');
        const productId = card.dataset.productId;

        if (selectedBundleItems.has(productId)) return;
        if (card.offsetWidth === 0 && card.offsetHeight === 0) return;

        const selector = card.querySelector('.bundle-variant-selector');
        let variantId, variantPrice, originalPrice, selectedProductTitle, discount, quantity, sku;

        if (selector) {
            const selectedOption = selector.options[selector.selectedIndex];
            variantId = parseInt(selectedOption.value, 10);
            variantPrice = getDiscountPrice(selectedOption);
            originalPrice = getItemPrice(selectedOption);
            selectedProductTitle = selectedOption.text.split(' - ')[0].trim();
            discount = parseInt(input.dataset.discount, 10) || 0;
            quantity = parseInt(input.dataset.quantity, 10) || 1;
            sku =  input.dataset.sku || "";
        } else {
            variantId = parseInt(input.dataset.variantId, 10);
            variantPrice = getDiscountPrice(input);
            originalPrice = getItemPrice(input);
            selectedProductTitle = input.dataset.defaultTitle;
            discount = parseInt(input.dataset.discount, 10) || 0;
            quantity = parseInt(input.dataset.quantity, 10) || 1;
            sku =  input.dataset.sku || "";
        }

        if (variantId) {
            selectedBundleItems.set(productId, {
                variantId: variantId,
                price: variantPrice,
                originalPrice: originalPrice,
                title: selectedProductTitle,
                discount: discount,
                quantity: quantity,
                sku: sku
            });
            card.classList.add('selected_item'); 
        }
    });

    updateBundleSummary();
}

function handleSelection(input) {
  try {
    const card = input.closest('.bundle-item-card');
    const productId = card.dataset.productId;
    const sku = input.dataset.sku || "";
    const groupId = card.dataset.groupId; 
    const selector = card.querySelector('.bundle-variant-selector');
    let variantId, variantPrice, originalPrice, selectedProductTitle, discountAmount;

    const quantityDisplayInput = card.querySelector('.bundle-item-quantity-display');
    let quantity = 1; 

    if (quantityDisplayInput) {
        quantity = parseInt(quantityDisplayInput.value, 10) || 1; 
        if (quantity < 1) {
            quantity = 1;
            quantityDisplayInput.value = 1; 
        }
    }

    if (selector) {
        const selectedOption = selector.options[selector.selectedIndex];
        variantId = parseInt(selectedOption.value, 10);
        variantPrice = getDiscountPrice(selectedOption);
        originalPrice = getItemPrice(selectedOption);
        selectedProductTitle = selectedOption.text.split(' - ')[0].trim();
    } else {
        variantId = parseInt(input.dataset.variantId, 10);
        variantPrice = getDiscountPrice(input);
        originalPrice = getItemPrice(input);
        selectedProductTitle = input.dataset.defaultTitle;
        discountAmount = input ? parseFloat(input.dataset.discount || 0) : 0; 
    }

    if (input.checked) {
      if (input.type === 'radio') {
          const idsToRemove = [];
          selectedBundleItems.forEach((item, id) => {
              const itemCard = document.querySelector(`.bundle-item-card[data-product-id="${id}"]`);
              if (itemCard && itemCard.dataset.groupId === groupId) {
                   idsToRemove.push(id);
              }
          });
          idsToRemove.forEach(id => selectedBundleItems.delete(id));
          
          document.querySelectorAll(`.bundle-item-card[data-group-id="${groupId}"]`).forEach(siblingCard => {
              siblingCard.classList.remove('selected_item');
          });
      }

      selectedBundleItems.set(productId, { 
        variantId: variantId, 
        price: variantPrice,
        originalPrice: originalPrice, 
        title: selectedProductTitle, 
        discount: discountAmount,
        quantity: quantity,
        sku: sku
      });
      card.classList.add('selected_item');
    } else {
      selectedBundleItems.delete(productId);
      card.classList.remove('selected_item');
    }
    
    checkAndAutoSelectMatchingPackage();
    updateBundleSummary();
    saveBundleToLocalStorage();
  } catch (e) {
    console.error("CRITICAL CRASH in handleSelection!", e);
    if (input.checked) { input.checked = false; }
  }
}

async function getInitialMainProductPrice(timeoutMs = 5000, intervalMs = 100) {
    const startTime = Date.now();
    const selector = '.product-page-info__price';

    const checkAndProcess = () => {
        const priceElement = document.querySelector(selector);
        if (priceElement) {
            const spanChildren = priceElement.querySelectorAll(':scope > span');
            const spanCount = spanChildren.length;
            let discountText = null;
            let defaultText = null;
            
            for(let i = 0; i < spanCount; i++){
                if (i === 0) {
                    defaultText = spanChildren[0].textContent || spanChildren[0].innerText;
                } else if(i === 1){
                    discountText = spanChildren[1].textContent || spanChildren[1].innerText;
                } else {
                    defaultText = spanChildren[0].textContent || spanChildren[0].innerText;
                }
            }
            
            if (defaultText !== null) {
                let cleanedPrice = defaultText.trim().replace(/[A-Za-z\$,\s]/g, ''); 
                let priceValue = parseFloat(cleanedPrice);
                if (isNaN(priceValue)) { priceValue = 0; }
                let price = Math.round(priceValue * 100);

                const cleanedDiscount = discountText ? String(discountText).trim().replace(/[A-Za-z\$,\s]/g, '') : 0;
                let discountValue = parseFloat(cleanedDiscount);
                if (isNaN(discountValue)) { discountValue = 0; }
                let discount = Math.round(discountValue * 100);

                let priceArray = {'price': price, 'discount': discount};
                if (!isNaN(price) && price > 0) {
                    return priceArray;
                }
            }
        }
        return null; 
    };

    return new Promise(resolve => {
        const initialPrice = checkAndProcess();
        if (initialPrice !== null) return resolve(initialPrice);

        const interval = setInterval(() => {
            const foundPrice = checkAndProcess();
            if (foundPrice !== null) {
                clearInterval(interval);
                return resolve(foundPrice);
            }
            if (Date.now() - startTime > timeoutMs) {
                clearInterval(interval);
                return resolve({'price': 0, 'discount': 0}); 
            }
        }, intervalMs);
    });
}

function checkAndAutoSelectMatchingPackage() {
  const localPackageButtons = document.querySelectorAll('.package-select-btn');
  if (!localPackageButtons.length) return;

  let selectedIds = [];
  if (typeof selectedBundleItems !== 'undefined') {
    selectedIds = Array.from(selectedBundleItems.keys()).map(id => id.toString().trim());

  }

  localPackageButtons.forEach(button => {
    
    const targetIdsString = button.getAttribute('data-target-product-ids');
    if (!targetIdsString) return;

    const packageIds = targetIdsString.split(',').map(id => id.trim());
    const isPackageFullySelected = packageIds.every(id => selectedIds.includes(id));

    if (isPackageFullySelected) {
      button.classList.add('selected_item');
    } else {
      button.classList.remove('selected_item');
    }
  });
}



// Intercept package automation arrays safely on the document layout
function handleGlobalPackageClick(event) {
    const packageBtn = event.target.closest('.package-select-btn');
    if (!packageBtn) return;

    event.preventDefault();
    
    const targetIdsString = packageBtn.getAttribute('data-target-product-ids');
    if (!targetIdsString) return;
    const targetProductIds = targetIdsString.split(',');

    if (typeof selectedBundleItems !== 'undefined' && selectedBundleItems.clear) {
        selectedBundleItems.clear();
    }

    document.querySelectorAll('.bundle-selection-input').forEach(input => {
        input.checked = false;
        const card = input.closest('.bundle-item-card');
        if (card) { card.classList.remove('selected_item'); }
    });

    targetProductIds.forEach(id => {
        const matchingInputs = document.querySelectorAll(`.bundle-selection-input[data-product-id="${id}"]`);
        matchingInputs.forEach(input => {
            input.checked = true;
            if (typeof handleSelection === 'function') { handleSelection(input); }
        });
    });

    if (typeof updateBundleSummary === 'function') { updateBundleSummary(); }
    if (typeof saveBundleToLocalStorage === 'function') { saveBundleToLocalStorage(); }

    const targetForm = getActiveShopifyForm();
    if (targetForm) {
        targetForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    document.querySelectorAll('.package-select-btn').forEach(b => b.classList.remove('selected_item'));
    packageBtn.classList.add('selected_item'); 
}

document.removeEventListener('click', handleGlobalPackageClick);
document.addEventListener('click', handleGlobalPackageClick);

async function updateBundleSummary() {
    let totalCount = 0;
    const mainProductPriceCents = await getInitialMainProductPrice(); 
    
    const mainOriginalPriceCents = parseInt(mainProductPriceCents.price, 10) || 0;
    const discountDisplay = parseInt(mainProductPriceCents.discount, 10) || 0;
    const activePackageButton = document.querySelector('.package-select-btn.selected_item');
    
    let componentOriginalPriceCents = 0; 
    let productsDiscount = 0;
    let packageDiscount = 0; 

    if (activePackageButton) {  
        const packageDiscountRaw = parseFloat(activePackageButton.getAttribute('data-discount') || 0);
        if (packageDiscountRaw > 0) {
            const newPackageDiscountCents = Math.round(packageDiscountRaw * 100);
            packageDiscount = isNaN(newPackageDiscountCents) ? 0 : newPackageDiscountCents;
        }
    }

    selectedBundleItems.forEach((item) => {
        const quantity = item.quantity || 1;
        totalCount += quantity;
      
        console.log(item);
        const baseOriginalPrice = parseInt(item.originalPrice, 10) || 0;
        componentOriginalPriceCents += (baseOriginalPrice * quantity);
        
        const itemDiscountRate = parseFloat(item.discount) || 0; 
        if (itemDiscountRate > 0) {
            const itemDiscountCents = Math.round(itemDiscountRate * quantity);
            productsDiscount += isNaN(itemDiscountCents) ? 0 : itemDiscountCents;
        }
    });

    const totalDiscountCents = productsDiscount + packageDiscount;
    const baseOriginalTotalCents = mainOriginalPriceCents + componentOriginalPriceCents;
    const totalWithDiscountCents = baseOriginalTotalCents - totalDiscountCents;

    const locale = (typeof Shopify !== 'undefined' && Shopify.locale) ? Shopify.locale : 'en-AU';
    const currency = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) ? Shopify.currency.active : 'AUD';

    console.log("mainProductPriceCents: "+mainOriginalPriceCents);
      console.log("componentOriginalPriceCents: "+componentOriginalPriceCents);
      console.log("packageDiscount: "+packageDiscount);
      console.log("productsDiscount: "+productsDiscount);

    let formatter;
    try { 
        formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: currency }); 
    } catch (e) {
        formatter = { format: (cents) => `$${(cents / 100).toFixed(2)}` };
    }
    
    const formatCurrencyString = (cents) => {
        let formatted = formatter.format(cents / 100);
        return formatted.includes('$') ? formatted.split('$')[1] : formatted;
    };

    if (summaryDiscount) summaryDiscount.classList.add('d-none');
    let savingsText = '0.00';

    const hasDiscount = totalDiscountCents > 0 || discountDisplay > 0;
    if (hasDiscount) {
        savingsText = formatCurrencyString(totalDiscountCents);
    }
      
    const mainProductForm = getActiveShopifyForm();
    if (mainProductForm) {
        const submitButton = mainProductForm.querySelector('button[type="submit"]');
        if (submitButton) {
            submitButton.textContent = `Add to cart`;
        }
    }
    
    if (summaryCount) {
        summaryCount.textContent = totalCount > 0 ? `+ ${totalCount} Bundle - Save $${savingsText}` : `Selected: 0 item(s)`;
    }

    if (summaryDiscount && summaryTotal) {
        if (hasDiscount) {
          summaryDiscount.classList.remove('d-none');
          summaryTotal.classList.add('strikethrough');
        } else {
          summaryDiscount.classList.add('d-none');  
          summaryTotal.classList.remove('strikethrough');
        }
    }
    
    if (summaryTotal) {
        summaryTotal.textContent = `$${formatCurrencyString(baseOriginalTotalCents)}`;
    }
    
    if (summaryDiscount && hasDiscount) {
        summaryDiscount.textContent = `$${formatCurrencyString(totalWithDiscountCents)}`;
    }
}

function handleUnifiedCartSubmission(event) {
    const mainProductForm = getActiveShopifyForm();
    if (!mainProductForm) return;

    const submitButton = mainProductForm.querySelector('.js-product-button-add-to-cart') || mainProductForm.querySelector('button[type="submit"]');
    
    if (event.type === 'click' && event.target && typeof event.target.closest === 'function') {
        if (!event.target.closest('.js-product-button-add-to-cart')) return; 
    }

    event.preventDefault(); 
    event.stopImmediatePropagation();

    if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Adding Bundle...';
    }

    const itemsPayload = [];
    
    // --- 🌟 FOOLPROOF URL VARIANT FALLBACK EXTRACTION 🌟 ---
    // 1. Try traditional DOM query tree parameters
    let idElement = mainProductForm.querySelector('input[name="id"], select[name="id"], [data-js-product-variant-input]') ||
                    document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
                    document.querySelector('input[name="id"]');
    
    let mainVariantId = idElement ? parseInt(idElement.value, 10) : null;

    // 2. Ultimate Override: If DOM values are missing or zero, read the active variant directly from the browser's current URL
    if (!mainVariantId || isNaN(mainVariantId)) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = urlParams.get('variant');
        if (urlVariantId) {
            mainVariantId = parseInt(urlVariantId, 10);
        }
    }

    // 3. Absolute Baseline Fallback: If the user hasn't toggled a variant yet (meaning no ?variant= in URL),
    // grab the default variant metadata tracking object assigned by the native liquid layout template if present
    if (!mainVariantId || isNaN(mainVariantId)) {
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
            mainVariantId = window.ShopifyAnalytics.meta.product.variants[0]?.id;
        } else if (window.meta && window.meta.product) {
            mainVariantId = window.meta.product.variants[0]?.id;
        }
    }

    const quantityInput = mainProductForm.querySelector('[name="quantity"]') || document.querySelector('[name="quantity"]');
    const mainQuantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    // Secure the main product variant into position index 0 of your dispatch list
    if (mainVariantId && !isNaN(mainVariantId)) {
        itemsPayload.push({
            id: mainVariantId,
            quantity: mainQuantity,
            properties: { '_bundle_main': 'true' }
        });
    } else {
        console.error("Bundle Submission Failure: Could not locate a valid variant ID anywhere on the page structure or URL parameters.");
    }

    // Process and pack accessories from your Map allocation arrays
    if (typeof selectedBundleItems !== 'undefined' && selectedBundleItems.size > 0) {
        selectedBundleItems.forEach(item => {
            const componentVariantId = parseInt(item.variantId, 10);
            const componentQuantity = Math.round(item.quantity || 1);
            
            // Deduplication checkpoint guard to guarantee index 0 isn't processed twice
            if (componentVariantId && !isNaN(componentVariantId) && componentVariantId !== mainVariantId) {
                itemsPayload.push({
                    id: componentVariantId,
                    quantity: componentQuantity
                });
            }
        });
    }

    console.log('Final Dispatched Payload Verification:', JSON.stringify({ items: itemsPayload }));

    if (itemsPayload.length === 0) {
        if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = 'Add Bundle to Cart';
        }
        alert("Please select options before checking out.");
        return; 
    }

    fetch('/cart/add.js', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsPayload }) 
    })
    .then(response => {
        if (!response.ok) {
            return response.json().then(errorData => {
                throw new Error(errorData.description || 'Failed to add items.');
            });
        }
        return response.json();
    })
    .then(() => {
        if (submitButton) submitButton.textContent = 'Added!';
        refreshCartContent(itemsPayload); 
    })
    .catch((error) => {
        console.error('Unified Cart API Processing Error:', error);
        if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = 'Error!';
        }
    });
}


window.addEventListener('beforeunload', () => {
    localStorage.removeItem('bundle_items');
    selectedBundleItems.clear();
});

function saveBundleToLocalStorage() {
    const itemsArray = Array.from(selectedBundleItems.entries()).map(([productId, item]) => ({
        id: productId.toString(), 
        qty: item.quantity || 1,
        sku: item.sku || ""
    }));
    localStorage.setItem('bundle_items', JSON.stringify(itemsArray));
}

function refreshCartContent(itemsPayload) {
    document.dispatchEvent(new CustomEvent('cart:add', { bubbles: true, detail: { items: itemsPayload } }));
    document.dispatchEvent(new CustomEvent('shopify:cart:add', { bubbles: true, detail: { items: itemsPayload } }));
    
    fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
            document.dispatchEvent(new CustomEvent('cart:afterAdd', { bubbles: true, detail: { cart: cart } }));
            if (window.theme && typeof window.theme.openCartDrawer === 'function') {
                window.theme.openCartDrawer();
            } else if (window.theme && window.theme.refreshCart) {
                window.theme.refreshCart();
            } else {
                setTimeout(() => { window.location.href = '/cart'; }, 500);
            }
        });
}

// Collapsible accordion drawers
document.addEventListener("DOMContentLoaded", () => {
  const triggers = document.querySelectorAll(".package-dropdown-trigger");
  triggers.forEach((trigger) => {
    trigger.addEventListener("click", function () {
      const parent = this.parentElement;
      const content = this.nextElementSibling;
      parent.classList.toggle("active");
      if (parent.classList.contains("active")) {
        content.style.maxHeight = content.scrollHeight + "px";
      } else {
        content.style.maxHeight = "0px";
      }
    });
  });
});

// --- JS INLINE RUNTIME BUTTON SHIMMER ENGINE ---
(function runInlineJavaScriptShimmer() {
  function applyShimmerLoop() {
    const cartButtons = document.querySelectorAll('.js-product-button-add-to-cart');
    if (!cartButtons.length) return;

    cartButtons.forEach(button => {
      if (button.hasAttribute('data-js-shimmer-active')) return;
      button.setAttribute('data-js-shimmer-active', 'true');

      button.style.position = 'relative';
      button.style.overflow = 'hidden';

      const shimmerStreak = document.createElement('span');
      Object.assign(shimmerStreak.style, {
        position: 'absolute',
        top: '0',
        left: '-150%',
        width: '40%',
        height: '100%',
        background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0) 100%)',
        transform: 'skewX(-25deg)',
        pointerEvents: 'none',
        transition: 'none'
      });

      button.appendChild(shimmerStreak);

      let position = -150;
      let isRunning = true;

      setInterval(() => {
        if (button.disabled) {
          shimmerStreak.style.left = '-150%';
          return;
        }

        if (isRunning) {
          position += 4; 
          shimmerStreak.style.left = position + '%';

          if (position >= 150) {
            isRunning = false;
            position = -150;
            shimmerStreak.style.left = '-150%';

            setTimeout(() => {
              isRunning = true;
            }, Math.floor(Math.random() * 2000) + 1500);
          }
        }
      }, 16);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyShimmerLoop);
  } else {
    applyShimmerLoop();
  }
  document.addEventListener('shopify:section:load', applyShimmerLoop);
  setInterval(applyShimmerLoop, 2000);
})();